/**
 * SOP Registry Parser
 * 
 * Parses the consolidated Markdown registry file (content/sops.md)
 * and provides typed access to Standard Operating Procedures.
 * 
 * This is the SINGLE SOURCE OF TRUTH for SOPs.
 */

import yaml from 'js-yaml';
import type {
  SOPRegistry,
  SOPRegistryEntry,
  SOPCategory,
  SOPAction,
  SOPInput,
  SOPDecisionPoint,
  SOPType,
  SOPPriority,
  SOPFilters,
  SOPCounts,
  PipelineBrand,
} from '../types';
import { getStageById } from './leadPipelineRegistry';
import { getRoleById } from './rolesRegistry';
import { getChannelSLAById, getStageSLAById } from './slaCatalogueRegistry';

// Import the raw markdown content
import sopsMarkdown from '../../content/sops.md?raw';

// =============================================================================
// Parsing Utilities
// =============================================================================

/**
 * Parse YAML block from fenced code block
 */
function parseYamlBlock(content: string): Record<string, unknown> | null {
  const yamlMatch = content.match(/```yaml\n([\s\S]*?)\n```/);
  if (!yamlMatch) return null;
  
  try {
    return yaml.load(yamlMatch[1]) as Record<string, unknown>;
  } catch (e) {
    console.error('Failed to parse YAML block:', e);
    return null;
  }
}

/**
 * Extract section content by heading (## level)
 */
function extractMainSection(content: string, heading: string): string {
  const startPattern = new RegExp(`## ${heading}`, 'i');
  const startMatch = content.match(startPattern);
  if (!startMatch || startMatch.index === undefined) return '';
  
  const startIdx = startMatch.index;
  const afterStart = content.substring(startIdx + startMatch[0].length);
  
  // Find the next ## heading that is NOT a ### subheading
  const nextSectionMatch = afterStart.match(/\n## (?!#)/);
  
  if (nextSectionMatch && nextSectionMatch.index !== undefined) {
    return content.substring(startIdx, startIdx + startMatch[0].length + nextSectionMatch.index);
  }
  
  return content.substring(startIdx);
}

// extractSubSection was removed as it's not currently used

/**
 * Extract content after a #### heading within a section
 */
function extractFourthLevelSection(content: string, heading: string): string {
  const headingPattern = new RegExp(`#### ${heading}[\\s\\S]*?(?=\\n+#### |\\n+### |\\n+## |\\n+# |$)`, 'i');
  const match = content.match(headingPattern);
  if (!match) return '';
  return match[0].replace(new RegExp(`#### ${heading}`, 'i'), '').trim();
}

/**
 * Parse bullet list from content
 */
function parseBulletList(content: string): string[] {
  const lines = content.split('\n');
  const items: string[] = [];
  
  for (const line of lines) {
    const match = line.match(/^[-*]\s+(.+)$/);
    if (match) {
      items.push(match[1].trim());
    }
  }
  
  return items;
}

/**
 * Parse Action/Step format from content
 */
function parseActionSteps(content: string): SOPAction[] {
  const actions: SOPAction[] = [];
  const lines = content.split('\n');
  
  let currentAction: SOPAction | null = null;
  
  for (const line of lines) {
    // Check for Action line: "- Action: Title"
    const actionMatch = line.match(/^[-*]\s+Action:\s*(.+)$/);
    if (actionMatch) {
      if (currentAction) {
        actions.push(currentAction);
      }
      currentAction = {
        action: actionMatch[1].trim(),
        steps: [],
      };
      continue;
    }
    
    // Check for Step line: "  - Step: Description"
    const stepMatch = line.match(/^\s+[-*]\s+Step:\s*(.+)$/);
    if (stepMatch && currentAction) {
      currentAction.steps.push({ step: stepMatch[1].trim() });
    }
  }
  
  // Don't forget the last action
  if (currentAction) {
    actions.push(currentAction);
  }
  
  return actions;
}

/**
 * Parse table rows from markdown table
 */
function parseTableRows(content: string): Record<string, string>[] {
  const lines = content.split('\n').filter(line => line.includes('|'));
  if (lines.length < 2) return [];
  
  // Extract headers from first row
  const headerLine = lines[0];
  const headers = headerLine.split('|').map(h => h.trim()).filter(h => h);
  
  // Skip separator row (second line)
  const dataLines = lines.slice(2);
  
  return dataLines.map(line => {
    const cells = line.split('|').map(c => c.trim()).filter(c => c);
    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      row[header.toLowerCase()] = cells[idx] || '';
    });
    return row;
  });
}

// =============================================================================
// Document Info Parser
// =============================================================================

interface DocumentInfo {
  version: string;
  lastUpdated: string;
  purpose: string;
  purposeAr: string;
}

function parseDocumentInfo(content: string): DocumentInfo {
  const section = extractMainSection(content, 'DOCUMENT INFO');
  const yamlData = parseYamlBlock(section);
  
  if (!yamlData || !yamlData.info) {
    console.warn('SOP Registry: Missing document info section');
    return {
      version: '1.0.0',
      lastUpdated: new Date().toISOString().split('T')[0],
      purpose: '',
      purposeAr: '',
    };
  }
  
  const info = yamlData.info as Record<string, unknown>;
  
  return {
    version: String(info.version || '1.0.0'),
    lastUpdated: String(info.lastUpdated || new Date().toISOString().split('T')[0]),
    purpose: String(info.purpose || ''),
    purposeAr: String(info.purposeAr || ''),
  };
}

// =============================================================================
// Categories Parser
// =============================================================================

function parseCategories(content: string): SOPCategory[] {
  const section = extractMainSection(content, 'CATEGORIES');
  const yamlData = parseYamlBlock(section);
  
  if (!yamlData || !yamlData.categories) {
    console.warn('SOP Registry: Missing categories section');
    return [];
  }
  
  const categoriesRaw = yamlData.categories as Array<Record<string, unknown>>;
  
  return categoriesRaw.map(cat => ({
    id: String(cat.id || ''),
    label: String(cat.label || ''),
    labelAr: String(cat.labelAr || ''),
    description: String(cat.description || ''),
    descriptionAr: String(cat.descriptionAr || ''),
  })).filter(cat => cat.id);
}

// =============================================================================
// SOP Parser
// =============================================================================

function parseSOPs(content: string): SOPRegistryEntry[] {
  const sops: SOPRegistryEntry[] = [];
  const validationErrors: string[] = [];
  
  // Find all SOP sections (### SOP: ...)
  const sopPattern = /### SOP:\s*[^\n]+\n\n```yaml\nmeta:\n([\s\S]*?)```([\s\S]*?)(?=\n### SOP:|\n## [A-Z][^\n]*\n|$)/g;
  let match;
  
  while ((match = sopPattern.exec(content)) !== null) {
    try {
      const yamlContent = `meta:\n${match[1]}`;
      const yamlData = yaml.load(yamlContent) as Record<string, unknown>;
      
      if (!yamlData || !yamlData.meta) continue;
      
      const meta = yamlData.meta as Record<string, unknown>;
      const sopId = String(meta.id || '');
      
      if (!sopId) continue;
      
      // Check for duplicate IDs
      if (sops.some(s => s.meta.id === sopId)) {
        validationErrors.push(`Duplicate SOP ID: ${sopId}`);
        continue;
      }
      
      // Validate stageId if provided
      const stageId = meta.stageId ? String(meta.stageId) : null;
      if (stageId && stageId !== 'null') {
        const pipelineStage = getStageById(stageId);
        if (!pipelineStage) {
          validationErrors.push(`SOP "${sopId}": Stage ID "${stageId}" not found in Lead Pipeline registry`);
        }
      }
      
      // Validate primaryOwnerRoleId
      const primaryOwnerRoleId = String(meta.primaryOwnerRoleId || '');
      if (primaryOwnerRoleId) {
        const role = getRoleById(primaryOwnerRoleId);
        if (!role) {
          validationErrors.push(`SOP "${sopId}": Role ID "${primaryOwnerRoleId}" not found in Roles registry`);
        }
      }
      
      // Validate supportingRoleIds
      const supportingRoleIds = (meta.supportingRoleIds as string[]) || [];
      supportingRoleIds.forEach(roleId => {
        if (roleId && !getRoleById(roleId)) {
          validationErrors.push(`SOP "${sopId}": Supporting role ID "${roleId}" not found in Roles registry`);
        }
      });
      
      // Validate relatedSlaIds
      const relatedSlaIds = (meta.relatedSlaIds as string[]) || [];
      relatedSlaIds.forEach(slaId => {
        if (slaId.startsWith('channel_')) {
          if (!getChannelSLAById(slaId)) {
            validationErrors.push(`SOP "${sopId}": Channel SLA "${slaId}" not found in SLA Catalogue`);
          }
        } else if (slaId.startsWith('stage_sla_')) {
          if (!getStageSLAById(slaId)) {
            validationErrors.push(`SOP "${sopId}": Stage SLA "${slaId}" not found in SLA Catalogue`);
          }
        }
      });
      
      // Parse the body content after YAML block
      const bodyContent = match[2] || '';
      
      // Parse Purpose
      const purposeSection = extractFourthLevelSection(bodyContent, 'Purpose');
      const purposeArSection = extractFourthLevelSection(bodyContent, 'Purpose \\(Arabic\\)');
      
      // Parse When to Use
      const whenToUseSection = extractFourthLevelSection(bodyContent, 'When to Use / Trigger');
      const whenToUseArSection = extractFourthLevelSection(bodyContent, 'When to Use / Trigger \\(Arabic\\)');
      
      // Parse Inputs table
      const inputsSection = extractFourthLevelSection(bodyContent, 'Inputs');
      const inputRows = parseTableRows(inputsSection);
      const inputs: SOPInput[] = inputRows.map(row => ({
        field: row.field || '',
        required: row.required === 'Yes' ? true : row.required === 'No' ? false : row.required,
        source: row.source || '',
      }));
      
      // Parse Steps (Action/Step format)
      const stepsSection = extractFourthLevelSection(bodyContent, 'Steps');
      const steps = parseActionSteps(stepsSection);
      
      // Parse Decision Points table
      const decisionSection = extractFourthLevelSection(bodyContent, 'Decision Points');
      const decisionRows = parseTableRows(decisionSection);
      const decisionPoints: SOPDecisionPoint[] = decisionRows.map(row => ({
        condition: row.condition || '',
        outcome: row.outcome || '',
        nextAction: row['next action'] || '',
      }));
      
      // Parse SLA Checks
      const slaChecksSection = extractFourthLevelSection(bodyContent, 'SLA Checks');
      const slaChecks = parseBulletList(slaChecksSection);
      
      // Parse Escalation & Exceptions
      const escalationSection = extractFourthLevelSection(bodyContent, 'Escalation & Exceptions');
      const escalationExceptions = parseBulletList(escalationSection);
      
      // Parse Audit/Logging Requirements
      const auditSection = extractFourthLevelSection(bodyContent, 'Audit/Logging Requirements');
      const auditRequirements = parseBulletList(auditSection);
      
      const sopEntry: SOPRegistryEntry = {
        meta: {
          id: sopId,
          type: (meta.type as SOPType) || 'stage',
          stageId: stageId === 'null' ? null : stageId,
          title: String(meta.title || ''),
          titleAr: String(meta.titleAr || ''),
          shortDesc: String(meta.shortDesc || ''),
          shortDescAr: String(meta.shortDescAr || ''),
          primaryOwnerRoleId,
          supportingRoleIds,
          relatedSlaIds,
          appliesToBrands: (meta.appliesToBrands as PipelineBrand[]) || ['Changan', 'Hyundai', 'Stellantis'],
          priority: (meta.priority as SOPPriority) || 'medium',
          triggers: (meta.triggers as string[]) || [],
          definitionOfDone: (meta.definitionOfDone as string[]) || [],
          lastUpdated: String(meta.lastUpdated || ''),
        },
        purpose: purposeSection.trim(),
        purposeAr: purposeArSection.trim(),
        whenToUse: parseBulletList(whenToUseSection),
        whenToUseAr: parseBulletList(whenToUseArSection),
        inputs,
        steps,
        decisionPoints,
        slaChecks,
        escalationExceptions,
        definitionOfDone: (meta.definitionOfDone as string[]) || [],
        auditRequirements,
      };
      
      sops.push(sopEntry);
    } catch (e) {
      console.error('Failed to parse SOP:', e);
    }
  }
  
  // Log validation errors
  if (validationErrors.length > 0) {
    console.warn('SOP Registry Validation Warnings:');
    validationErrors.forEach(err => console.warn(`  - ${err}`));
  }
  
  return sops;
}

// =============================================================================
// Main Parser Function
// =============================================================================

/**
 * Parse the complete SOP Registry
 */
function parseSOPRegistry(markdown: string): SOPRegistry {
  const docInfo = parseDocumentInfo(markdown);
  const categories = parseCategories(markdown);
  const sops = parseSOPs(markdown);
  
  return {
    version: docInfo.version,
    lastUpdated: docInfo.lastUpdated,
    purpose: docInfo.purpose,
    purposeAr: docInfo.purposeAr,
    categories,
    sops,
  };
}

// =============================================================================
// Cached Registry & Export Functions
// =============================================================================

let cachedRegistry: SOPRegistry | null = null;

/**
 * Get the parsed SOP Registry
 */
export function getSOPRegistry(): SOPRegistry {
  if (!cachedRegistry) {
    cachedRegistry = parseSOPRegistry(sopsMarkdown);
  }
  return cachedRegistry;
}

/**
 * Get all SOPs
 */
export function getAllSOPs(): SOPRegistryEntry[] {
  return getSOPRegistry().sops;
}

/**
 * Get a specific SOP by ID
 */
export function getSOPById(id: string): SOPRegistryEntry | undefined {
  return getAllSOPs().find(sop => sop.meta.id === id);
}

/**
 * Get all stage SOPs
 */
export function getStageSOPs(): SOPRegistryEntry[] {
  return getAllSOPs().filter(sop => sop.meta.type === 'stage');
}

/**
 * Get all scenario SOPs
 */
export function getScenarioSOPs(): SOPRegistryEntry[] {
  return getAllSOPs().filter(sop => sop.meta.type === 'scenario');
}

/**
 * Get SOPs for a specific pipeline stage
 */
export function getSOPsForStage(stageId: string): SOPRegistryEntry[] {
  return getAllSOPs().filter(sop => sop.meta.stageId === stageId);
}

/**
 * Get SOPs where a role is the primary owner
 */
export function getSOPsForRole(roleId: string): SOPRegistryEntry[] {
  return getAllSOPs().filter(sop => 
    sop.meta.primaryOwnerRoleId === roleId ||
    sop.meta.supportingRoleIds.includes(roleId)
  );
}

/**
 * Get SOPs where a role is the primary owner only
 */
export function getSOPsOwnedByRole(roleId: string): SOPRegistryEntry[] {
  return getAllSOPs().filter(sop => sop.meta.primaryOwnerRoleId === roleId);
}

/**
 * Get SOPs related to a specific SLA
 */
export function getSOPsForSLA(slaId: string): SOPRegistryEntry[] {
  return getAllSOPs().filter(sop => sop.meta.relatedSlaIds.includes(slaId));
}

/**
 * Filter SOPs by brand
 */
export function getSOPsForBrand(brand: PipelineBrand): SOPRegistryEntry[] {
  return getAllSOPs().filter(sop => sop.meta.appliesToBrands.includes(brand));
}

/**
 * Filter SOPs by priority
 */
export function getSOPsByPriority(priority: SOPPriority): SOPRegistryEntry[] {
  return getAllSOPs().filter(sop => sop.meta.priority === priority);
}

/**
 * Search SOPs by query and filters
 */
export function searchSOPs(query?: string, filters?: SOPFilters): SOPRegistryEntry[] {
  let sops = getAllSOPs();
  
  // Apply type filter
  if (filters?.type) {
    sops = sops.filter(sop => sop.meta.type === filters.type);
  }
  
  // Apply brand filter
  if (filters?.brand) {
    sops = sops.filter(sop => sop.meta.appliesToBrands.includes(filters.brand!));
  }
  
  // Apply stage filter
  if (filters?.stageId) {
    sops = sops.filter(sop => sop.meta.stageId === filters.stageId);
  }
  
  // Apply role filter
  if (filters?.roleId) {
    sops = sops.filter(sop => 
      sop.meta.primaryOwnerRoleId === filters.roleId ||
      sop.meta.supportingRoleIds.includes(filters.roleId!)
    );
  }
  
  // Apply priority filter
  if (filters?.priority) {
    sops = sops.filter(sop => sop.meta.priority === filters.priority);
  }
  
  // Apply search query
  if (query && query.trim()) {
    const searchLower = query.toLowerCase();
    sops = sops.filter(sop => 
      sop.meta.title.toLowerCase().includes(searchLower) ||
      sop.meta.titleAr.includes(query) ||
      sop.meta.shortDesc.toLowerCase().includes(searchLower) ||
      sop.meta.shortDescAr.includes(query) ||
      sop.purpose.toLowerCase().includes(searchLower) ||
      sop.meta.id.toLowerCase().includes(searchLower)
    );
  }
  
  return sops;
}

/**
 * List SOPs with filters (alias for searchSOPs)
 */
export function listSOPs(filters?: SOPFilters): SOPRegistryEntry[] {
  return searchSOPs(filters?.search, filters);
}

/**
 * Get SOP categories
 */
export function getSOPCategories(): SOPCategory[] {
  return getSOPRegistry().categories;
}

/**
 * Get SOP counts for summary display
 */
export function getSOPCounts(): SOPCounts {
  const sops = getAllSOPs();
  
  const priorities: SOPPriority[] = ['critical', 'high', 'medium', 'low'];
  const byPriority: { priority: SOPPriority; count: number }[] = priorities
    .map(p => ({ priority: p, count: sops.filter(s => s.meta.priority === p).length }))
    .filter(p => p.count > 0);
  
  return {
    totalSOPs: sops.length,
    stageSOPs: sops.filter(s => s.meta.type === 'stage').length,
    scenarioSOPs: sops.filter(s => s.meta.type === 'scenario').length,
    byPriority,
  };
}

/**
 * Validate SOP references exist in other registries
 * Returns array of validation errors (empty if all valid)
 */
export function validateSOPReferences(): string[] {
  const errors: string[] = [];
  const sops = getAllSOPs();
  
  sops.forEach(sop => {
    // Validate stageId
    if (sop.meta.stageId) {
      const stage = getStageById(sop.meta.stageId);
      if (!stage) {
        errors.push(`SOP "${sop.meta.id}": Stage ID "${sop.meta.stageId}" not found`);
      }
    }
    
    // Validate primaryOwnerRoleId
    if (sop.meta.primaryOwnerRoleId) {
      const role = getRoleById(sop.meta.primaryOwnerRoleId);
      if (!role) {
        errors.push(`SOP "${sop.meta.id}": Primary owner role "${sop.meta.primaryOwnerRoleId}" not found`);
      }
    }
    
    // Validate supportingRoleIds
    sop.meta.supportingRoleIds.forEach(roleId => {
      if (!getRoleById(roleId)) {
        errors.push(`SOP "${sop.meta.id}": Supporting role "${roleId}" not found`);
      }
    });
    
    // Validate relatedSlaIds
    sop.meta.relatedSlaIds.forEach(slaId => {
      if (slaId.startsWith('channel_')) {
        if (!getChannelSLAById(slaId)) {
          errors.push(`SOP "${sop.meta.id}": Channel SLA "${slaId}" not found`);
        }
      } else if (slaId.startsWith('stage_sla_')) {
        if (!getStageSLAById(slaId)) {
          errors.push(`SOP "${sop.meta.id}": Stage SLA "${slaId}" not found`);
        }
      }
    });
  });
  
  return errors;
}

/**
 * Export the raw registry for debugging
 */
export const registry = getSOPRegistry();

export default registry;
